import React, { useState, useEffect } from 'react';
import { Download, X, Share } from 'lucide-react';

const DISMISSED_KEY = 'pwa_install_dismissed';

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
}

function isInStandaloneMode() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [isIOSDevice, setIsIOSDevice] = useState(false);

  useEffect(() => {
    if (isInStandaloneMode() || localStorage.getItem(DISMISSED_KEY)) return;

    const ios = isIOS();
    setIsIOSDevice(ios);

    if (ios) {
      // iOS는 beforeinstallprompt 없음 — 3초 후 바로 안내 표시
      setTimeout(() => setShow(true), 3000);
    } else {
      function handleBeforeInstall(e) {
        e.preventDefault();
        setDeferredPrompt(e);
        setTimeout(() => setShow(true), 3000);
      }
      window.addEventListener('beforeinstallprompt', handleBeforeInstall);
      return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    }
  }, []);

  async function handleInstall() {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setShow(false);
    setDeferredPrompt(null);
  }

  function handleDismiss() {
    setShow(false);
    localStorage.setItem(DISMISSED_KEY, '1');
  }

  if (!show) return null;

  // iOS 안내 배너
  if (isIOSDevice) {
    return (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-sm">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <Download className="w-4 h-4 text-white" />
              </div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">홈 화면에 추가하기</p>
            </div>
            <button onClick={handleDismiss} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
          <ol className="space-y-1.5 text-xs text-gray-600 dark:text-gray-400">
            <li className="flex items-center gap-2">
              <span className="w-5 h-5 bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-full flex items-center justify-center font-bold flex-shrink-0">1</span>
              하단 <Share className="w-3.5 h-3.5 inline text-blue-500 mx-0.5" /> <span className="font-medium text-blue-500">공유</span> 버튼을 탭하세요
            </li>
            <li className="flex items-center gap-2">
              <span className="w-5 h-5 bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-full flex items-center justify-center font-bold flex-shrink-0">2</span>
              <span><span className="font-medium">"홈 화면에 추가"</span>를 선택하세요</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="w-5 h-5 bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-full flex items-center justify-center font-bold flex-shrink-0">3</span>
              오른쪽 상단 <span className="font-medium">"추가"</span>를 탭하세요
            </li>
          </ol>
          {/* 말풍선 화살표 */}
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white dark:bg-gray-800 border-r border-b border-gray-200 dark:border-gray-700 rotate-45" />
        </div>
      </div>
    );
  }

  // Chrome/Android/기타 배너
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-sm">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-4 flex items-center gap-3">
        <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center flex-shrink-0">
          <Download className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">앱으로 설치하기</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">홈 화면에 추가하면 더 빠르게 실행돼요</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleInstall}
            className="text-xs font-semibold text-white bg-primary-600 hover:bg-primary-700 px-3 py-1.5 rounded-lg transition-colors"
          >
            설치
          </button>
          <button
            onClick={handleDismiss}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
